import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDropdown } from '../../contexts/DropdownContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Search, Rss, DollarSign, Building2, Clock, ChevronLeft, ChevronRight,
  Send, Eye, Globe, X, CheckCircle, Loader2, RefreshCw, Users, Bookmark, BookmarkCheck, Target,
  Mail, ChevronDown, ChevronUp, Check, XCircle, Bell, MessageSquare, Sparkles, Info, Coins
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Competition level based on proposal count
const getCompetition = (count) => {
  if (count >= 6) return { label: 'High Demand', color: 'bg-red-100 text-red-700 border-red-200',       icon: '🔥' };
  if (count >= 3) return { label: 'Active',       color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '⚡' };
  if (count >= 1) return { label: 'Low Comp',     color: 'bg-blue-100 text-blue-700 border-blue-200',    icon: '📋' };
  return               { label: 'First In!',      color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: '🚀' };
};

// Deadline countdown
const getDeadlineInfo = (deadlineStr) => {
  if (!deadlineStr) return null;
  try {
    const dl   = new Date(deadlineStr);
    const diff = Math.ceil((dl - new Date()) / 86400000);
    if (diff < 0)   return { label: 'Expired',        color: 'text-gray-400',  bg: 'bg-gray-50',   urgent: false };
    if (diff === 0) return { label: 'Due Today!',      color: 'text-red-600',   bg: 'bg-red-50',    urgent: true };
    if (diff <= 3)  return { label: `${diff}d left`,   color: 'text-red-600',   bg: 'bg-red-50',    urgent: true };
    if (diff <= 7)  return { label: `${diff}d left`,   color: 'text-amber-600', bg: 'bg-amber-50',  urgent: false };
    return               { label: `${diff}d left`,     color: 'text-gray-500',  bg: 'bg-gray-50',   urgent: false };
  } catch { return null; }
};

const MOCK_BRIEFS = [
  { id: 'br_open_1', event: { show: 'Cityscape Dubai 2025 — Luxury Pavilion', city: 'Dubai', country: 'AE', month: '2025-03' }, stand: { w_m: 9, d_m: 9 }, budget: { min: 80000, max: 95000, currency: 'USD' }, deadline_at: '2025-02-20', proposals_count: 2, status: 'OPEN', category: 'Luxury' },
  { id: 'br_open_2', event: { show: 'Automechanika Frankfurt 2025', city: 'Frankfurt', country: 'DE', month: '2025-09' }, stand: { w_m: 12, d_m: 8 }, budget: { min: 100000, max: 120000, currency: 'EUR' }, deadline_at: '2025-03-05', proposals_count: 5, status: 'OPEN', category: 'Automotive' },
  { id: 'br_open_3', event: { show: 'Gulfood Dubai — Food Stand', city: 'Dubai', country: 'AE', month: '2025-02' }, stand: { w_m: 6, d_m: 4 }, budget: { min: 45000, max: 60000, currency: 'USD' }, deadline_at: '2025-02-18', proposals_count: 3, status: 'OPEN', category: 'Food & Beverage' },
  { id: 'br_open_4', event: { show: 'Hannover Messe — Industry 4.0', city: 'Hannover', country: 'DE', month: '2025-04' }, stand: { w_m: 15, d_m: 10 }, budget: { min: 130000, max: 160000, currency: 'EUR' }, deadline_at: '2025-04-01', proposals_count: 1, status: 'OPEN', category: 'Technology' },
  { id: 'br_open_5', event: { show: 'Istanbul Construction Fair 2025', city: 'Istanbul', country: 'TR', month: '2025-03' }, stand: { w_m: 6, d_m: 6 }, budget: { min: 30000, max: 40000, currency: 'TRY' }, deadline_at: '2025-03-20', proposals_count: 4, status: 'OPEN', category: 'Construction' },
  { id: 'br_open_6', event: { show: 'Medica Düsseldorf 2025', city: 'Düsseldorf', country: 'DE', month: '2025-11' }, stand: { w_m: 9, d_m: 6 }, budget: { min: 90000, max: 115000, currency: 'EUR' }, deadline_at: '2025-11-10', proposals_count: 0, status: 'OPEN', category: 'Healthcare' },
];

const COUNTRY_NAMES = { DE: 'Germany', TR: 'Türkiye', AE: 'UAE', ES: 'Spain', NL: 'Netherlands', FR: 'France', GB: 'UK', US: 'USA', SG: 'Singapore', IT: 'Italy' };
const FALLBACK_CURRENCIES = ['EUR', 'USD', 'GBP', 'AED', 'TRY'];
const PAGE_SIZE = 6;

/* ─── INVITATION BANNER ─────────────────────────────────── */
const MOCK_INVITATIONS = [
  { id: 'inv_m1', brief_id: 'br_open_1', exhibitor_name: 'GlobalTech Exhibitions', show_title: 'Cityscape Dubai 2025 — Luxury Pavilion', city: 'Dubai', budget_max: 95000, currency: 'USD', message: 'We would love you to submit a quote for our upcoming Dubai stand.', status: 'pending', invited_at: new Date(Date.now() - 86400000 * 2).toISOString() },
  { id: 'inv_m2', brief_id: 'br_open_4', exhibitor_name: 'Planet Industries GmbH', show_title: 'Hannover Messe — Industry 4.0', city: 'Hannover', budget_max: 160000, currency: 'EUR', message: '', status: 'pending', invited_at: new Date(Date.now() - 86400000).toISOString() },
];

const InvitationBanner = ({ token, isMockMode }) => {
  const [invitations, setInvitations] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (isMockMode) { setInvitations(MOCK_INVITATIONS); return; }
    if (!token) return;
    axios.get(`${API}/contractor/invitations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInvitations((r.data?.items || []).filter(i => i.status === 'pending')))
      .catch(() => {});
  }, [token, isMockMode]);

  const handleAction = async (invId, action) => {
    setProcessing(invId);
    try {
      if (!isMockMode) {
        await axios.patch(`${API}/contractor/invitations/${invId}`, { status: action }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await new Promise(r => setTimeout(r, 600));
      }
      setInvitations(prev => prev.filter(i => i.id !== invId));
      toast.success(action === 'accepted' ? 'Davet kabul edildi! Brief Board\'dan teklif verebilirsiniz.' : 'Davet reddedildi.');
    } catch {
      toast.error('İşlem başarısız oldu.');
    } finally { setProcessing(null); }
  };

  if (invitations.length === 0) return null;

  return (
    <div className="rounded-xl border border-green-200 bg-green-50 overflow-hidden" data-testid="invitation-banner">
      {/* Header */}
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-green-100/60 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-8 w-8 items-center justify-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-50" />
            <span className="relative inline-flex h-8 w-8 rounded-full bg-green-600 text-white text-xs font-bold items-center justify-center">
              {invitations.length}
            </span>
          </span>
          <div className="text-left">
            <p className="text-sm font-bold text-green-800">
              {invitations.length === 1 ? '1 yeni davetiniz var' : `${invitations.length} yeni davetiniz var`}
            </p>
            <p className="text-xs text-green-600">Exhibitorlar sizinle çalışmak istiyor — kabul ederek teklif gönderebilirsiniz</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-green-600 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-green-600 flex-shrink-0" />}
      </button>

      {/* Cards */}
      {expanded && (
        <div className="px-4 pb-4 space-y-2.5">
          {invitations.map(inv => (
            <div key={inv.id} className="bg-white rounded-xl p-4 shadow-sm border border-green-100 flex flex-wrap gap-3 items-start"
              data-testid={`invitation-card-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-gray-900">{inv.show_title || inv.brief_id}</p>
                  <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{inv.city}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  <span className="font-medium text-gray-700">{inv.exhibitor_name}</span>
                  {inv.budget_max && <span> · Bütçe: {inv.budget_max.toLocaleString()} {inv.currency}</span>}
                </p>
                {inv.message && (
                  <p className="text-xs text-gray-500 mt-1 italic line-clamp-1">"{inv.message}"</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={processing === inv.id}
                  onClick={() => handleAction(inv.id, 'declined')}
                  data-testid={`decline-invite-${inv.id}`}
                  className="h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 gap-1"
                >
                  {processing === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                  Reddet
                </Button>
                <Button
                  size="sm"
                  disabled={processing === inv.id}
                  onClick={() => handleAction(inv.id, 'accepted')}
                  data-testid={`accept-invite-${inv.id}`}
                  className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white gap-1"
                >
                  {processing === inv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                  Kabul Et
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── SUBMIT QUOTE MODAL ────────────────────────────────── */
const SubmitQuoteModal = ({ brief, onClose, onSubmitted, token, isMockMode }) => {
  const { currencies } = useDropdown();
  // Deduplicate currency codes
  const currencyCodes = useMemo(() => {
    if (currencies.length > 0) {
      const seen = new Set();
      currencies.forEach(c => c.currency_code && seen.add(c.currency_code));
      return [...seen].sort();
    }
    return FALLBACK_CURRENCIES;
  }, [currencies]);
  const [form, setForm] = useState({ amount: '', currency: brief.budget?.currency || 'EUR', delivery_days: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [usedCredits, setUsedCredits] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load credit balance
  useEffect(() => {
    if (!isMockMode && token) {
      axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCreditBalance(r.data?.balance ?? null))
        .catch(() => {});
    }
  }, [token, isMockMode]);

  // Smart currency warning: exhibitor's base currency vs current selection
  const exhibitorCurrency = brief.exhibitor_base_currency;
  const showCurrencyWarning = exhibitorCurrency && exhibitorCurrency !== form.currency;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 800));
      } else {
        const res = await axios.post(`${API}/contractor/proposals`, {
          brief_id: brief.id,
          amount: parseFloat(form.amount),
          currency: form.currency,
          delivery_days: form.delivery_days ? parseInt(form.delivery_days) : undefined,
          notes: form.notes || undefined,
        }, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.used_credits) setUsedCredits(true);
      }
      setDone(true);
      onSubmitted(brief.id);
      setTimeout(onClose, 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (detail.startsWith('LIMIT_REACHED:')) {
        setError(detail.replace('LIMIT_REACHED:', ''));
      } else {
        setError(detail || 'Submission failed. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const sqm = (brief.stand?.w_m || 0) * (brief.stand?.d_m || 0);

  if (done) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Submitted!</h3>
        {usedCredits && (
          <div className="mb-3 flex items-center justify-center gap-2 text-amber-600 text-sm font-medium">
            <Coins className="w-4 h-4" />
            <span>10 kredi kullanıldı</span>
          </div>
        )}
        <p className="text-gray-500 text-sm">Your proposal has been sent to the exhibitor. You'll be notified when they respond.</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Submit Quote</h3>
            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{brief.event?.show}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Brief Summary */}
        <div className="mx-5 mt-4 bg-green-50 border border-green-100 rounded-xl p-4 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-black text-gray-900">{sqm} sqm</p>
            <p className="text-xs text-gray-500">Stand Area</p>
          </div>
          <div>
            <p className="text-lg font-black text-gray-900">{brief.budget?.min?.toLocaleString()}–{brief.budget?.max?.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Budget ({brief.budget?.currency})</p>
          </div>
          <div>
            <p className="text-lg font-black text-gray-900">{brief.deadline_at ? new Date(brief.deadline_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : '—'}</p>
            <p className="text-xs text-gray-500">Deadline</p>
          </div>
        </div>

        {/* Smart Currency Warning */}
        {showCurrencyWarning && (
          <div className="mx-5 mt-3 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-blue-800 font-medium">
                Bu organizasyon tercih ettiği para birimi: <strong>{exhibitorCurrency}</strong>
              </p>
              <p className="text-xs text-blue-600 mt-0.5">Teklifi {exhibitorCurrency} cinsinden vermek kabul şansını artırabilir.</p>
            </div>
            <button
              onClick={() => set('currency', exhibitorCurrency)}
              className="text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-lg shrink-0 transition-colors"
            >
              {exhibitorCurrency}'ye Geç
            </button>
          </div>
        )}

        {/* Credit balance info */}
        {creditBalance !== null && (
          <div className="mx-5 mt-2 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            <Coins className="w-3.5 h-3.5 shrink-0" />
            <span>Kredi bakiyeniz: <strong>{creditBalance} kredi</strong> — limit aşıldığında her teklif 10 kredi harcar</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Your Quote Amount *</label>
              <Input type="number" min="0" step="100" value={form.amount} onChange={e => set('amount', e.target.value)}
                placeholder="e.g. 92000" required className="h-10" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Currency</label>
              <select value={form.currency} onChange={e => set('currency', e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500">
              {currencyCodes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Estimated Delivery (days)</label>
            <Input type="number" min="1" value={form.delivery_days} onChange={e => set('delivery_days', e.target.value)}
              placeholder="e.g. 21" className="h-10" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Cover Note / Your Pitch</label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Briefly describe your approach, relevant experience, and why you're the right fit..." rows={4} />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{error}</div>}
          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Quote
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─── MAIN ──────────────────────────────────────────────── */
const ContractorBriefBoard = () => {
  const { isMockMode } = useDataMode();
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myOrgId = user?.org_memberships?.[0]?.org_id || '';
  const [briefs, setBriefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [quoteTarget, setQuoteTarget] = useState(null);
  const [submitted, setSubmitted] = useState(new Set());
  const [bookmarks, setBookmarks] = useState(new Set());
  const [bookmarking, setBookmarking] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'saved'
  const [highMatchOnly, setHighMatchOnly] = useState(false);
  const [sortBy, setSortBy] = useState('match');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [quickFilter, setQuickFilter] = useState(''); // 'last_week' | 'deadline_week' | 'deadline_month'
  const [messaging, setMessaging] = useState(null); // brief id being messaged
  const [aiMatches, setAiMatches]   = useState({});  // { brief_id: {score, reason} }
  const [aiAnalyzing, setAiAnalyzing] = useState(false);

  const handleAIMatch = async () => {
    if (!token || isMockMode || briefs.length === 0) return;
    setAiAnalyzing(true);
    try {
      const ids = briefs.slice(0, 10).map(b => b.id);
      const res = await axios.post(
        `${API}/contractor/briefs/ai-match`,
        { brief_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiMatches(prev => ({ ...prev, ...(res.data?.matches || {}) }));
    } catch { /* silent fail */ }
    finally { setAiAnalyzing(false); }
  };

  const handleMessageExhibitor = async (brief, e) => {
    if (e) e.stopPropagation();
    const exhibitorOrgId = brief.org_id || '';
    if (!exhibitorOrgId || !token) { navigate('/app/contractor/messages'); return; }
    setMessaging(brief.id);
    try {
      const res = await axios.post(
        `${API}/messages/conversations`,
        { brief_id: brief.id, exhibitor_org_id: exhibitorOrgId, contractor_org_id: myOrgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/app/contractor/messages?conv=${res.data.conversation_id}`);
    } catch { navigate('/app/contractor/messages'); }
    finally { setMessaging(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 400));
        setBriefs(MOCK_BRIEFS);
        setTotal(MOCK_BRIEFS.length);
      } else {
        const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
        if (countryFilter !== 'all') params.country = countryFilter;
        const res = await axios.get(`${API}/contractor/briefs/open`, {
          headers: { Authorization: `Bearer ${token}` }, params,
        });
        setBriefs(res.data.items || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      setBriefs(MOCK_BRIEFS);
      setTotal(MOCK_BRIEFS.length);
    } finally { setLoading(false); }
  }, [isMockMode, token, page, countryFilter]);

  useEffect(() => { load(); }, [load]);

  // Load bookmarks
  useEffect(() => {
    if (!token || isMockMode) return;
    axios.get(`${API}/contractor/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setBookmarks(new Set(r.data?.brief_ids || [])))
      .catch(() => {});
  }, [token, isMockMode]);

  const toggleBookmark = async (briefId, e) => {
    e.stopPropagation();
    if (!token || isMockMode) return;
    setBookmarking(briefId);
    const isBookmarked = bookmarks.has(briefId);
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/contractor/bookmarks/${briefId}`, { headers: { Authorization: `Bearer ${token}` } });
        setBookmarks(prev => { const n = new Set(prev); n.delete(briefId); return n; });
      } else {
        await axios.post(`${API}/contractor/bookmarks/${briefId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setBookmarks(prev => new Set([...prev, briefId]));
      }
    } catch { /* ignore */ } finally { setBookmarking(null); }
  };

  const allCountries = ['all', ...new Set([...MOCK_BRIEFS.map(b => b.event?.country), ...briefs.map(b => b.event?.country)].filter(Boolean))];
  const allCategories = ['all', ...new Set([...MOCK_BRIEFS.map(b => b.category), ...briefs.map(b => b.category)].filter(Boolean))];

  const filtered = briefs.filter(b => {
    const q = search.toLowerCase();
    const matchesSearch = !q || b.event?.show?.toLowerCase().includes(q) || b.event?.city?.toLowerCase().includes(q) || b.category?.toLowerCase().includes(q);
    const matchesCountry = countryFilter === 'all' || b.event?.country === countryFilter;
    const matchesCategory = categoryFilter === 'all' || b.category === categoryFilter;
    const matchesSaved = viewMode === 'all' || bookmarks.has(b.id);
    const matchesHighMatch = !highMatchOnly ||
      (b.match_score != null && b.match_score >= 75) ||
      (aiMatches[b.id] && aiMatches[b.id].score >= 4);
    const bMax = b.budget?.max || 0;
    const matchesBudgetMin = !budgetMin || bMax >= parseInt(budgetMin, 10);
    const matchesBudgetMax = !budgetMax || bMax <= parseInt(budgetMax, 10);
    // Quick filters
    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 86400000);
    const oneWeekAhead = new Date(now.getTime() + 7 * 86400000);
    const oneMonthAhead = new Date(now.getTime() + 30 * 86400000);
    const matchesQuick = !quickFilter || (() => {
      if (quickFilter === 'last_week') return b.created_at && new Date(b.created_at) >= oneWeekAgo;
      if (quickFilter === 'deadline_week') return b.deadline && new Date(b.deadline) <= oneWeekAhead && new Date(b.deadline) >= now;
      if (quickFilter === 'deadline_month') return b.deadline && new Date(b.deadline) <= oneMonthAhead && new Date(b.deadline) >= now;
      return true;
    })();
    return matchesSearch && matchesCountry && matchesCategory && matchesSaved && matchesHighMatch && matchesBudgetMin && matchesBudgetMax && matchesQuick;
  // Sort: boosted first, then user-selected sort
  }).sort((a, b) => {
    const aB = a.is_boosted && (!a.boost_expires_at || new Date(a.boost_expires_at) > new Date());
    const bB = b.is_boosted && (!b.boost_expires_at || new Date(b.boost_expires_at) > new Date());
    if ((bB ? 1 : 0) !== (aB ? 1 : 0)) return (bB ? 1 : 0) - (aB ? 1 : 0);
    if (sortBy === 'match')        return (b.match_score || 0) - (a.match_score || 0);
    if (sortBy === 'newest')       return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === 'budget_high')  return (b.budget?.max || 0) - (a.budget?.max || 0);
    if (sortBy === 'deadline')     return new Date(a.deadline || '2099') - new Date(b.deadline || '2099');
    return 0;
  });

  const totalPages = isMockMode ? Math.ceil(filtered.length / PAGE_SIZE) : Math.ceil(total / PAGE_SIZE);
  const paged = isMockMode ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered;

  return (
    <div className="space-y-6">
      {quoteTarget && (
        <SubmitQuoteModal
          brief={quoteTarget}
          onClose={() => setQuoteTarget(null)}
          onSubmitted={(id) => setSubmitted(prev => new Set([...prev, id]))}
          token={token}
          isMockMode={isMockMode}
        />
      )}

      {/* Invitation Banner */}
      <InvitationBanner token={token} isMockMode={isMockMode} />

      <div className="flex flex-wrap items-center justify-between gap-3">        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('briefs.briefBoard')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">Browse open stand requests and submit proposals</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              data-testid="tab-all-briefs"
              onClick={() => { setViewMode('all'); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === 'all' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              All Briefs
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${viewMode === 'all' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                {isMockMode ? filtered.length : total}
              </span>
            </button>
            <button
              data-testid="tab-saved-briefs"
              onClick={() => { setViewMode('saved'); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1 ${viewMode === 'saved' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <BookmarkCheck className="h-3.5 w-3.5" />
              Saved
              {bookmarks.size > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${viewMode === 'saved' ? 'bg-amber-100 text-amber-700' : 'bg-amber-100 text-amber-600'}`}>
                  {bookmarks.size}
                </span>
              )}
            </button>
          </div>
          {isMockMode && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">MOCK</span>}
          <button
            data-testid="high-match-toggle"
            onClick={() => { setHighMatchOnly(p => !p); setPage(1); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-all ${highMatchOnly ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-600'}`}
          >
            <Target className="h-3.5 w-3.5" />
            High Match Only
          </button>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </Button>
          {!isMockMode && (
            <Button
              size="sm" onClick={handleAIMatch}
              disabled={aiAnalyzing || briefs.length === 0}
              data-testid="ai-match-btn"
              className="gap-1.5 h-8 bg-violet-600 hover:bg-violet-700 text-white"
            >
              {aiAnalyzing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5" />}
              {aiAnalyzing ? 'Analiz ediliyor...' : 'AI Analiz'}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search brief, city or category..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-gray-50 border-gray-200" />
          </div>
          <select value={countryFilter} onChange={e => { setCountryFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            {allCountries.map(c => <option key={c} value={c}>{c === 'all' ? 'All Countries' : (COUNTRY_NAMES[c] || c)}</option>)}
          </select>
          <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
            data-testid="sort-by-select"
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            <option value="match">Sort: Match Score</option>
            <option value="newest">Sort: Newest First</option>
            <option value="budget_high">Sort: Budget High→Low</option>
            <option value="deadline">Sort: Deadline Soonest</option>
          </select>
          <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
            data-testid="category-filter-select"
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500">
            {allCategories.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
          </select>
          <div className="flex items-center gap-1.5">
            <input type="number" value={budgetMin} onChange={e => { setBudgetMin(e.target.value); setPage(1); }}
              placeholder="Min €" data-testid="budget-min-input"
              className="w-20 px-2 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500" />
            <span className="text-gray-400 text-xs">–</span>
            <input type="number" value={budgetMax} onChange={e => { setBudgetMax(e.target.value); setPage(1); }}
              placeholder="Max €" data-testid="budget-max-input"
              className="w-20 px-2 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          {(categoryFilter !== 'all' || budgetMin || budgetMax) && (
            <button onClick={() => { setCategoryFilter('all'); setBudgetMin(''); setBudgetMax(''); setPage(1); }}
              data-testid="clear-filters-btn"
              className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1">
              Clear filters
            </button>
          )}
        </div>
        {/* Quick Filter Chips */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'last_week',      label: 'Added Last 7 Days',    icon: '✨' },
            { key: 'deadline_week',  label: 'Deadline This Week',   icon: '⚡' },
            { key: 'deadline_month', label: 'Deadline This Month',  icon: '📅' },
          ].map(({ key, label, icon }) => (
            <button key={key} onClick={() => { setQuickFilter(q => q === key ? '' : key); setPage(1); }}
              data-testid={`quick-filter-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                quickFilter === key
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-700'
              }`}>
              <span>{icon}</span>{label}
              {quickFilter === key && <span className="text-green-100 ml-0.5">×</span>}
            </button>
          ))}
          {(quickFilter || categoryFilter !== 'all' || budgetMin || budgetMax) && (
            <button onClick={() => { setQuickFilter(''); setCategoryFilter('all'); setBudgetMin(''); setBudgetMax(''); setPage(1); }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium px-2 transition-colors">
              Reset all
            </button>
          )}
        </div>
          <span className="text-sm text-gray-400 ml-auto">
            {viewMode === 'saved' ? `${filtered.length} saved` : `${(categoryFilter !== 'all' || budgetMin || budgetMax || quickFilter || highMatchOnly || search) ? filtered.length : (isMockMode ? filtered.length : total)} results`}
          </span>
        </CardContent>
      </Card>

      {/* Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-52 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : paged.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          {viewMode === 'saved' ? (
            <>
              <BookmarkCheck className="h-12 w-12 mx-auto mb-3 text-amber-300" />
              <p className="font-medium text-gray-600">No saved briefs yet</p>
              <p className="text-sm mt-1">Click the bookmark icon on any brief to save it here</p>
              <button onClick={() => setViewMode('all')}
                className="mt-3 text-sm text-green-600 font-semibold hover:underline">
                Browse all briefs →
              </button>
            </>
          ) : (
            <>
              <Rss className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No open briefs found</p>
              <p className="text-sm mt-1">Check back later or adjust your filters</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paged.map(b => {
            const sqm        = ((b.stand?.w_m || 0) * (b.stand?.d_m || 0)).toFixed(0);
            const isSubmitted = submitted.has(b.id);
            const competition = getCompetition(b.proposals_count || 0);
            const deadline    = getDeadlineInfo(b.deadline_at);
            const isBoosted   = b.is_boosted && (!b.boost_expires_at || new Date(b.boost_expires_at) > new Date());
            return (
              <Card key={b.id} className={`border-0 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5 duration-200 ${deadline?.urgent ? 'ring-1 ring-red-200' : ''} ${isBoosted ? 'ring-2 ring-orange-400 ring-offset-1' : ''}`}>
                <CardContent className="p-5">
                  {/* Boost header stripe */}
                  {isBoosted && (
                    <div className="bg-gradient-to-r from-orange-500 to-amber-400 -mx-5 -mt-5 mb-4 px-5 py-2 rounded-t-xl flex items-center gap-2">
                      <span className="text-white text-xs font-bold flex items-center gap-1">
                        ⭐ ÖNE ÇIKAN BRIEF
                      </span>
                    </div>
                  )}
                  {/* Top row: category + competition + match score */}
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {b.category
                        ? <Badge className="bg-green-50 text-green-700 text-xs border-0">{b.category}</Badge>
                        : <Badge className="bg-gray-100 text-gray-500 text-xs border-0">Open Brief</Badge>
                      }
                      <Badge className={`text-[10px] px-1.5 py-0.5 border ${competition.color}`}>
                        {competition.icon} {competition.label}
                      </Badge>
                      {b.match_score != null && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-bold border ${
                            b.match_score >= 75 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            b.match_score >= 50 ? 'bg-blue-50 text-blue-600 border-blue-200' :
                            'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                          title="Match Score: how well this brief matches your coverage area"
                        >
                          {b.match_score >= 75 ? 'High' : b.match_score >= 50 ? 'Medium' : 'Low'} Match
                        </span>
                      )}
                      {/* AI Match Score */}
                      {aiMatches[b.id] && (
                        <span
                          data-testid={`ai-score-${b.id}`}
                          title={aiMatches[b.id].reason}
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold border flex items-center gap-0.5 cursor-help ${
                            aiMatches[b.id].score >= 4 ? 'bg-violet-50 text-violet-700 border-violet-200' :
                            aiMatches[b.id].score === 3 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            'bg-gray-50 text-gray-500 border-gray-200'
                          }`}
                        >
                          <Sparkles className="h-2.5 w-2.5" />
                          {'★'.repeat(aiMatches[b.id].score)}{'☆'.repeat(5 - aiMatches[b.id].score)}
                        </span>
                      )}
                    </div>
                    {deadline && (
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0 ${deadline.bg} ${deadline.color}`}>
                        {deadline.urgent ? '⏰ ' : ''}{deadline.label}
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 leading-snug mb-3 line-clamp-2">
                    {b.event?.show}
                  </h3>
                  <div className="space-y-1.5 text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 text-gray-400" />
                      {b.event?.city}, {COUNTRY_NAMES[b.event?.country] || b.event?.country}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-gray-400" />
                      {b.stand?.w_m}×{b.stand?.d_m}m ({sqm} sqm)
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3 w-3" />{b.proposals_count || 0} proposals submitted
                    </div>
                    <div className="flex items-center gap-1.5 font-bold text-gray-900 text-sm mt-1">
                      <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                      {b.budget?.min?.toLocaleString()} – {b.budget?.max?.toLocaleString()} {b.budget?.currency}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-gray-50">
                    <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setSelected(b)}>
                      <Eye className="h-3 w-3" />Details
                    </Button>
                    <button
                      data-testid={`bookmark-btn-${b.id}`}
                      onClick={(e) => toggleBookmark(b.id, e)}
                      disabled={bookmarking === b.id}
                      className={`h-8 w-8 flex items-center justify-center rounded-md border transition-colors shrink-0 ${bookmarks.has(b.id) ? 'bg-amber-50 border-amber-300 text-amber-500' : 'border-gray-200 text-gray-400 hover:border-amber-300 hover:text-amber-400'}`}
                      title={bookmarks.has(b.id) ? 'Remove bookmark' : 'Save brief'}
                    >
                      {bookmarks.has(b.id) ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
                    </button>
                    {!isMockMode && b.org_id && (
                      <button
                        data-testid={`message-exhibitor-btn-${b.id}`}
                        onClick={(e) => handleMessageExhibitor(b, e)}
                        disabled={messaging === b.id}
                        className="h-8 w-8 flex items-center justify-center rounded-md border border-blue-200 text-blue-500 hover:bg-blue-50 transition-colors shrink-0"
                        title="Message Exhibitor"
                      >
                        {messaging === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <Button size="sm" disabled={isSubmitted}
                      className={`flex-1 h-8 text-xs gap-1 ${isSubmitted ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                      onClick={() => !isSubmitted && setQuoteTarget(b)}>
                      {isSubmitted ? <><CheckCircle className="h-3 w-3" />Submitted</> : <><Send className="h-3 w-3" />Submit Quote</>}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, isMockMode ? filtered.length : total)} / {isMockMode ? filtered.length : total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            {[...Array(Math.min(totalPages, 7))].map((_, i) => (
              <Button key={i} variant={page === i + 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(i + 1)}>{i + 1}</Button>
            ))}
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold">Brief Details</h3>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {[
                ['Show / Event', selected.event?.show],
                ['Location', `${selected.event?.city}, ${COUNTRY_NAMES[selected.event?.country] || selected.event?.country}`],
                ['Event Month', selected.event?.month],
                ['Stand Size', `${selected.stand?.w_m}×${selected.stand?.d_m}m (${(selected.stand?.w_m * selected.stand?.d_m).toFixed(0)} sqm)`],
                ['Budget', `${selected.budget?.min?.toLocaleString()} – ${selected.budget?.max?.toLocaleString()} ${selected.budget?.currency}`],
                ['Submission Deadline', new Date(selected.deadline_at).toLocaleDateString('en-GB')],
                ['Open Proposals', `${selected.proposals_count || 0} proposals`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{k}</span>
                  <span className="font-medium text-right max-w-[60%]">{v}</span>
                </div>
              ))}
              {selected.notes && (
                <div className="pt-2">
                  <p className="text-xs text-gray-400 mb-1">Exhibitor Notes</p>
                  <p className="text-sm bg-gray-50 rounded-lg p-3 text-gray-700">{selected.notes}</p>
                </div>
              )}
            </div>
            <Button className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white gap-2 h-11"
              onClick={() => { setSelected(null); setQuoteTarget(selected); }}>
              <Send className="h-4 w-4" />Submit Quote for This Brief
            </Button>
            {!isMockMode && selected?.org_id && (
              <Button variant="outline" className="w-full mt-2 gap-2 h-9 text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="message-exhibitor-modal-btn"
                disabled={messaging === selected?.id}
                onClick={(e) => handleMessageExhibitor(selected, e)}>
                {messaging === selected?.id
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <MessageSquare className="h-4 w-4" />}
                Message Exhibitor
              </Button>
            )}
            {aiMatches[selected?.id] && (
              <div className="mt-3 p-3 bg-violet-50 rounded-xl border border-violet-100 flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-violet-600 mt-0.5 shrink-0" />
                <div>
                  <span className="text-xs font-bold text-violet-700">
                    AI Uyum: {'★'.repeat(aiMatches[selected.id].score)}{'☆'.repeat(5 - aiMatches[selected.id].score)}
                  </span>
                  <p className="text-xs text-violet-600 mt-0.5">{aiMatches[selected.id].reason}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorBriefBoard;
